import { useConvexAuth } from 'convex/react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useAppAuth } from '@/hooks/use-app-auth';
import {
  billingPackageQueryString,
  readStoredDesiredBillingPackage,
} from '@/lib/billing/billing-package';
import {
  DATA_IMPORT_SETUP_PATH,
  IS_DATA_IMPORT,
} from '@/lib/runtime/feature-flags';
import { resolveSandboxGate } from '../features/sandbox/lib/sandbox-job';
import { useSandboxJob } from '../features/sandbox/lib/use-sandbox-job';

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function useAuthGuard() {
  const { isSignedIn, isLoaded } = useAppAuth();
  const { isAuthenticated } = useConvexAuth();
  const { user, isLoading, hasCompany } = useCurrentUser();
  // A skipped query outside a sandbox build — no subscription, answers `null`.
  const sandboxJob = useSandboxJob();

  return {
    isLoaded,
    isSignedIn,
    isAuthenticated,
    isLoading,
    hasCompany,
    user,
    sandboxJob,
  };
}

export function ProtectedRoute() {
  const {
    isLoaded,
    isSignedIn,
    isAuthenticated,
    isLoading,
    hasCompany,
    user,
    sandboxJob,
  } = useAuthGuard();

  if (!isLoaded) {
    return <FullPageSpinner />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!isAuthenticated || isLoading) {
    return <FullPageSpinner />;
  }

  // Before the onboarding redirect: in a sandbox an account is normally not
  // created by onboarding (`startOnboarding` throws
  // SANDBOX_ONBOARDING_DISABLED unless the user asked for a blank sandbox), it
  // is imported from production — and an import that is running or failed
  // leaves a graph no app page can be trusted with. The gate also covers the
  // no-company case, which is what keeps the redirect below (→ `/onboarding`,
  // → back here) from becoming a loop in a sandbox build. `onboarding` needs
  // no branch of its own: it falls through to that very redirect, which
  // `OnboardingRoute` now lets through for a blank sandbox.
  if (IS_DATA_IMPORT) {
    const gate = resolveSandboxGate({
      hasUserRow: !!user,
      hasCompany,
      job: sandboxJob,
    });
    if (gate === 'loading') return <FullPageSpinner />;
    if (gate === 'setup') {
      return <Navigate to={DATA_IMPORT_SETUP_PATH} replace />;
    }
  }

  if (!user || !hasCompany) {
    if (user?.suspendedCompanies?.length) {
      return <Navigate to="/access-suspended" replace />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}

export function OnboardingRoute() {
  const location = useLocation();
  const {
    isLoaded,
    isSignedIn,
    isAuthenticated,
    isLoading,
    hasCompany,
    user,
    sandboxJob,
  } = useAuthGuard();

  if (!isLoaded) {
    return <FullPageSpinner />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!isAuthenticated || isLoading) {
    return <FullPageSpinner />;
  }

  // In a sandbox the account normally comes from production, and the wizard
  // has nothing to do — except for the user who chose an empty environment.
  // The gate is what tells the two apart: `onboarding` renders the wizard,
  // everything else goes back to the setup page.
  if (IS_DATA_IMPORT) {
    const gate = resolveSandboxGate({
      hasUserRow: !!user,
      hasCompany,
      job: sandboxJob,
    });
    if (gate === 'loading') return <FullPageSpinner />;
    if (gate === 'setup') {
      return <Navigate to={DATA_IMPORT_SETUP_PATH} replace />;
    }
  }

  if (hasCompany) {
    // Re-read at the no-company → company transition: onboarding may have
    // refined the marketing package with the user's module choices since this
    // route mounted. The stored package is also the "not through the plan step"
    // signal; subscribe clears it on completion. Fiduciaires skip that step.
    const desiredPackage = readStoredDesiredBillingPackage();
    const plan = user?.company?.plan ?? 'free';
    const isEditingModules =
      new URLSearchParams(location.search).get('edit') === 'modules';
    if (
      isEditingModules &&
      plan === 'free' &&
      desiredPackage &&
      user?.company &&
      !user.fiduciaire
    ) {
      return <Outlet context={{ company: user.company }} />;
    }
    if (plan === 'free' && desiredPackage && !user?.fiduciaire) {
      return (
        <Navigate
          to={`/welcome/subscribe${billingPackageQueryString(desiredPackage)}`}
          replace
        />
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (user?.suspendedCompanies?.length) {
    return <Outlet />;
  }

  return <Outlet />;
}

/**
 * Guard for `/sandbox/setup` (sandbox builds only): signed in and known to
 * Convex, and nothing else — the whole point of that page is that the user
 * has no account in this deployment yet. Deliberately NOT `useAuthGuard()`:
 * the page runs its own `useCurrentUser` / `useSandboxJob`, and the guard
 * would only duplicate both subscriptions.
 */
export function SandboxRoute() {
  const { isLoaded, isSignedIn } = useAppAuth();
  const { isAuthenticated } = useConvexAuth();

  if (!isLoaded) {
    return <FullPageSpinner />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!isAuthenticated) {
    return <FullPageSpinner />;
  }

  return <Outlet />;
}

export function PublicRoute() {
  const { isSignedIn, isLoaded } = useAppAuth();

  if (!isLoaded) {
    return <FullPageSpinner />;
  }

  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
